<template>
    <div class="w-full flex flex-col">
        <div class="font-semibold grid grid-cols-7 md:grid-cols-7 gap-12 py-12 px-16 border-b border-primary-20">
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('multi-posting.channels.title') }}</div>
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('general.price') }}</div>
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('general.status') }}</div>
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('multi-posting.channels.start_to_end') }}</div>
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('multi-posting.channels.processing_time') }}</div>
            <div class="first-letter:uppercase font-semibold text-black">{{ $t('general.requested_by') }}</div>
            <div class="first-letter:uppercase font-semibold text-black text-end">{{ $t('multi-posting.channels.action') }}</div>
        </div>

        <div class="overflow-y-auto">
            <div v-for="channel in channels" class="grid grid-cols-7 md:grid-cols-7 gap-12 py-8 px-16 text-black odd:bg-primary-5">
                <div class="font-semibold flex items-center">{{ channel.name }}</div>
                <div class="flex items-center" v-if="!!channel.price.amount">
                    <currency/>
                    {{ channel.price.amount.toFixed(2) }}
                </div>
                <div class="flex items-center" v-else>{{ $t('multi-posting.price.free') }}</div>
                <div class="flex items-center">
                    <badge
                        :content="formatStatus(channel.status)"
                        :display="statusColor(channel.status)"
                    />
                </div>
                <div class="flex items-center" v-html="fromTo(channel)"></div>
                <div class="flex items-center">
                    <svg-icon type="schedule" class="w-18 h-18 -mt-2 mr-4" />
                    <span class="lowercase">{{ deliverTime(channel) }}</span>
                </div>
                <div class="flex items-center">
                    <p v-html="requestedByAndOn"></p>
            </div>
            <div class="flex items-center justify-end gap-4">
                <template v-for="action in channelActions(channel)">
                    <simple-button
                        :disabled="action.disabled"
                        @click="performAction(channel, action.action)"
                        type="link"
                        look="icon"
                        :icon="action.icon"
                        small
                    />
                </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SvgIcon from "../ui/SvgIcon.vue";
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import moment from 'moment';
import Currency from "../Currency.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import Badge from "../ui/Badge.vue";
import Tooltip from "../navigation/Tooltip.vue";


export default {
    name: 'CampaignChannels',
    components: {Tooltip, Badge, SimpleButton, SvgIcon, Currency },
    props: {
        campaign: {type: Object, required: true}
    },
    data: function() {
        return {
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale', 'dateFormat']),
        duration() {
            const duration = this.extra().duration
            return duration ? duration : 0;
        },
        postLink() {
            const postLink = this.extra().post_link
            return postLink ? postLink : ""
        },
        requestedByAndOn() {
            const requestedBy = this.extra().requested_by
            return requestedBy ? `${this.$t('general.by')} <span class="font-semibold">${requestedBy}</span><br>${this.$t('general.on')} <span class="font-semibold">${this.format(this.campaign.created_at)}</span>` : ""
        },
        timeLocale() {
            const mapping = {
                'en': 'en-gb',
                'de': 'de-de',
                'fr': 'fr-fr',
                'nl': 'nl-nl',
                'it': 'it-it',
                'po': 'pl-pl',
            }
            return mapping[this.locale] || this.locale;
        },
        channels() {
            return this.campaign.channels;
        }
    },
    methods: {
        deliverTime(product) {
            // always in hours
            if (!product.time_to_process && !product.time_to_setup) return this.$t('multi-posting.duration.immediately');
            const duration = product.time_to_process + product.time_to_setup;
            if (duration < 24)
                return `${duration} ${this.$tc('general.hours', duration)}`;
            const days = Math.floor(duration / 24);
            return `${days} ${this.$tc('general.working_days', days)}`;
        },
        fromTo(channel) {
            // for the start date we choose to display the campaigns start,
            // because this is the date we send the campaign to VONQ, and we cannot
            // guarantee that the channel will start based on the time_to_setup or time_to_process
            const channelStartDate = this.startDate(channel);
            // for the end date, we
            const channelEndDate = this.channelEndDate(channel);
            return `<p>${this.$t('general.from')} <span class="font-semibold">${channelStartDate}</span> </br> ${this.$t('general.to')} <span class="font-semibold">${channelEndDate}</span></p>`
        },
        startDate(channel) {
            if (channel.delivered_on) {
                return this.validDate(channel.delivered_on) ? this.format(channel.delivered_on) : '';
            }
            return this.campaignStartDate();
        },
        campaignStartDate() {
            const startDate = this.campaign.start_date;
            return this.validDate(startDate) ? this.format(startDate) : '';
        },
        channelEndDate(channel) {
            let startDate = channel.delivered_on || this.campaign.start_date;
            if (this.validDate(startDate)) {
                startDate = moment(startDate);
            } else {
                return '';
            }

            if (channel.duration?.period) {
                const duration = channel.duration.period;
                const range = channel.duration.range ?? 'days';
                // By this point, startDate will always be a moment object
                return this.format(startDate.add(duration, range));
            } else {
                return this.format(startDate.add(this.duration, 'days'));
            }
        },
        channelActions(channel) {
            let actions = [];
            if (channel.type !== 'socialChannel') {
                actions.push({
                    icon: 'link',
                    action: 'visit_link',
                    disabled: !channel.job_board_link || channel.status !== 'online',
                });
            }
            return actions;
        },
        extra() {
            const extra = this.campaign.extra
            return extra ? extra : {}
        },
        format(date) {
            return date ? moment(date).locale(this.timeLocale).format('L') : "";
        },
        formatStatus(status) {
            if (!status) return ''
            return status.length > 1 ? status.charAt(0).toUpperCase() + status.slice(1) : status
        },
        statusColor(status) {
            if (!status) return ''
            return status === 'online' ? 'success' : status === 'in progress' ? 'warning' : 'danger';
        },
        validDate(date) {
            return date != null && !date.startsWith('0000')
        },
        performAction(channel, action) {
            if (!channel || channel.status !== 'online') return;
            if (action === "visit_link") {
                // open in new tab
                let url = channel.job_board_link;
                if (!url.startsWith('http')) {
                    url = 'https://' + url;
                }
                window.open(url, '_blank', 'popup');
            }
        }
    },
}
</script>
